/**
 * FERP Portal - Standards Pack Curator UI
 *
 * Route: /ferp-core/compliance/standards-packs
 * Access: Backend scope-guarded (standards:read, standards:apply).
 *
 * Displays installed standards packs and AI-curated curator recommendations.
 * Operators can accept (apply) or reject recommendations.
 *
 * GraphQL operations:
 *   - Query.listStandardsPacks   — installed packs for this tenant
 *   - Query.recommendStandardsPacks — AI curator recommendations
 *   - Query.applyStandardsPack   — apply (accept) a recommendation
 *
 * DRR-055: Dumb frontend — renders only what backend returns.
 * DRR-102: Mutations are scope-guarded at the backend resolver.
 * DRR-128: Write-time minimization — no speculative writes.
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Badge,
  Table,
  Button,
  Tabs,
  TextInput,
  Textarea,
  Modal,
  Alert,
  Card,
  Progress,
  Loader,
  Pill,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch,
  IconAlertTriangle,
  IconShieldCheck,
  IconPackage,
  IconCheck,
  IconX,
  IconRobot,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  useListStandardsPacksForCuratorQuery,
  useRecommendStandardsPacksForCuratorQuery,
  useApplyStandardsPackQueryLazyQuery,
  type StandardsPackListItem,
  type StandardsPackRecommendation,
  type ApplyStandardsPackResult,
  type ApplyStandardsPackInput,
  type RecommendationResult,
  GqlStandardsPackKind,
} from '@/generated/graphql';
import { useAuth } from '@/auth/AuthContext';

// GraphQL operations defined in:
// - queries-part3.graphql: ListStandardsPacksForCurator, RecommendStandardsPacksForCurator, ApplyStandardsPackQuery

// ============================================================================
// HELPERS
// ============================================================================

function kindColor(kind: string): string {
  switch (kind) {
    case GqlStandardsPackKind.POLICY:
      return 'blue';
    case GqlStandardsPackKind.SCHEMA:
      return 'green';
    case GqlStandardsPackKind.TERMINOLOGY:
      return 'violet';
    case GqlStandardsPackKind.INTEGRATION:
      return 'orange';
    default:
      return 'gray';
  }
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ============================================================================
// INSTALLED PACKS TAB
// ============================================================================

interface InstalledPacksTabProps {
  packs: StandardsPackListItem[];
  loading: boolean;
  error: string | null;
}

function InstalledPacksTab({ packs, loading, error }: InstalledPacksTabProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return packs;
    const q = search.toLowerCase();
    return packs.filter(
      (p) =>
        p.standard.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        p.applicableDomains.some((d) => d.toLowerCase().includes(q)),
    );
  }, [packs, search]);

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">Loading installed packs…</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        Failed to load standards packs: {error}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search by standard, ID, or domain…"
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 400 }}
      />

      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Standard</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Domains</Table.Th>
              <Table.Th>Resource Types</Table.Th>
              <Table.Th>Added</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    {packs.length === 0
                      ? 'No standards packs installed.'
                      : 'No packs match your search.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((pack) => (
              <Table.Tr key={pack.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>
                      {pack.standard}
                    </Text>
                    {pack.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {pack.description}
                      </Text>
                    )}
                    <Text size="xs" ff="monospace" c="dimmed">
                      {pack.id}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{pack.version}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={kindColor(pack.kind)} variant="light">
                    {pack.kind}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="wrap">
                    {pack.applicableDomains.slice(0, 3).map((d) => (
                      <Pill key={d} size="xs">
                        {d}
                      </Pill>
                    ))}
                    {pack.applicableDomains.length > 3 && (
                      <Text size="xs" c="dimmed">
                        +{pack.applicableDomains.length - 3}
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="wrap">
                    {pack.applicableResourceTypes.slice(0, 2).map((r) => (
                      <Pill key={r} size="xs" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {r}
                      </Pill>
                    ))}
                    {pack.applicableResourceTypes.length > 2 && (
                      <Text size="xs" c="dimmed">
                        +{pack.applicableResourceTypes.length - 2}
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {formatDate(pack.createdAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

// ============================================================================
// REJECT MODAL
// ============================================================================

interface RejectModalProps {
  opened: boolean;
  onClose: () => void;
  packId: string | null;
  packName: string | null;
  onRejected: () => void;
}

function RejectModal({ opened, onClose, packId, packName, onRejected }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Rejection is recorded locally — backend accepts/rejects are both routed through apply
  // with a "reject" flag in the reason. For now we capture the reason and call onRejected.
  // In production this would call a rejectStandardsPackRecommendation mutation.
  const [loading, setLoading] = useState(false);

  async function handleReject() {
    if (!packId || !reason.trim()) return;
    setError(null);
    setLoading(true);
    try {
      // Backend rejection mutation not yet generated — log intent and signal parent.
      // When the mutation is generated, replace this with useMutation hook call.
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
      setReason('');
      onRejected();
      onClose();
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Reject Recommendation: ${packName ?? packId ?? ''}`}
      size="sm"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Provide a reason for rejecting this recommendation. This is recorded for audit purposes
          (DRR-128 write-time minimization — only what protects the tenant is written).
        </Text>
        <Textarea
          label="Rejection reason"
          placeholder="e.g. Not applicable to this tenant's jurisdiction…"
          required
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={14} />}>
            {error}
          </Alert>
        )}
      </Stack>
      <Group justify="space-between" mt="lg">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="red"
          loading={loading}
          disabled={!reason.trim()}
          onClick={handleReject}
        >
          Reject Recommendation
        </Button>
      </Group>
    </Modal>
  );
}

// ============================================================================
// CURATOR RECOMMENDATIONS TAB
// ============================================================================

interface CuratorRecommendationsTabProps {
  tenantId: string;
}

function CuratorRecommendationsTab({ tenantId }: CuratorRecommendationsTabProps) {
  // recommendStandardsPacks requires a workflow JSON context.
  // Pass an empty object to get tenant-wide recommendations.
  const { data, loading, error, refetch } = useRecommendStandardsPacksForCuratorQuery({
    variables: { tenant: tenantId, workflow: {} },
    fetchPolicy: 'cache-and-network',
  });

  const [applyPack] = useApplyStandardsPackQueryLazyQuery();

  const { user } = useAuth();

  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [selectedPackName, setSelectedPackName] = useState<string | null>(null);
  const [acceptLoading, setAcceptLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const recommendations: StandardsPackRecommendation[] =
    data?.recommendStandardsPacks?.recommendations ?? [];

  async function handleAccept(rec: StandardsPackRecommendation) {
    if (!tenantId || !user?.id) return;
    setActionError(null);
    setSuccessMessage(null);
    setAcceptLoading(rec.packId);
    try {
      const result = await applyPack({
        variables: {
          input: {
            packId: rec.packId,
            tenant: tenantId,
            actor: user.id,
            workflowId: data?.recommendStandardsPacks?.workflowId ?? '',
            forceOverrideConflicts: false,
          },
        },
      });
      const applied = result.data?.applyStandardsPack;
      if (applied?.success) {
        setSuccessMessage(`Applied "${rec.packName}" successfully.`);
        refetch();
      } else {
        setActionError(`Apply returned success=false for pack "${rec.packId}".`);
      }
    } catch (err) {
      setActionError(String(err));
    } finally {
      setAcceptLoading(null);
    }
  }

  function handleOpenReject(rec: StandardsPackRecommendation) {
    setSelectedPackId(rec.packId);
    setSelectedPackName(rec.packName);
    openReject();
  }

  if (loading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">Fetching AI-curated recommendations…</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        Failed to load recommendations: {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Alert
        color="blue"
        variant="light"
        icon={<IconRobot size={16} />}
      >
        AI-curated standards pack recommendations based on your active workflows and compliance
        frameworks. Accepting a recommendation applies the pack to your tenant. Rejecting records
        your reason for audit (DRR-128).
      </Alert>

      {actionError && (
        <Alert color="red" icon={<IconAlertTriangle size={14} />} withCloseButton onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {successMessage && (
        <Alert color="green" icon={<IconCheck size={14} />} withCloseButton onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {recommendations.length === 0 && (
        <Paper withBorder p="xl">
          <Stack align="center" gap="xs">
            <IconInfoCircle size={32} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed" size="sm" ta="center">
              No curator recommendations available for this tenant at this time.
            </Text>
          </Stack>
        </Paper>
      )}

      {recommendations.map((rec) => (
        <Card key={rec.packId} withBorder p="md">
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text fw={600} size="sm">
                  {rec.packName}
                </Text>
                <Text size="xs" ff="monospace" c="dimmed">
                  {rec.packId}
                </Text>
              </Stack>
              <Badge
                size="sm"
                color={rec.priority <= 1 ? 'red' : rec.priority <= 3 ? 'orange' : 'gray'}
                variant="light"
              >
                Priority {rec.priority}
              </Badge>
            </Group>

            <Text size="sm" c="dimmed">
              {rec.reason}
            </Text>

            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Confidence
                </Text>
                <Text size="xs" fw={500}>
                  {Math.round(rec.confidence * 100)}%
                </Text>
              </Group>
              <Progress
                value={rec.confidence * 100}
                color={rec.confidence >= 0.8 ? 'green' : rec.confidence >= 0.5 ? 'yellow' : 'red'}
                size="sm"
                radius="xl"
              />
            </Stack>

            {rec.autoApply && (
              <Badge size="xs" color="teal" variant="dot">
                Auto-apply eligible
              </Badge>
            )}

            <Group justify="flex-end" gap="xs">
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconX size={12} />}
                onClick={() => handleOpenReject(rec)}
                disabled={acceptLoading === rec.packId}
              >
                Reject
              </Button>
              <Button
                size="xs"
                color="green"
                leftSection={<IconCheck size={12} />}
                loading={acceptLoading === rec.packId}
                onClick={() => handleAccept(rec)}
              >
                Accept
              </Button>
            </Group>
          </Stack>
        </Card>
      ))}

      <RejectModal
        opened={rejectOpened}
        onClose={closeReject}
        packId={selectedPackId}
        packName={selectedPackName}
        onRejected={() => refetch()}
      />
    </Stack>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function StandardsPackPage() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const {
    data: packsData,
    loading: packsLoading,
    error: packsError,
  } = useListStandardsPacksForCuratorQuery({
    variables: { tenant: tenantId },
    skip: !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const packs = packsData?.listStandardsPacks ?? [];

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <Stack gap={2}>
          <Group gap="xs">
            <IconPackage size={24} />
            <Title order={2}>Standards Packs</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Manage installed compliance standards packs and review AI-curated recommendations.
            Apply packs to activate their rules and validation constraints for this tenant.
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge size="sm" variant="outline" color="blue">
            {packs.length} installed
          </Badge>
        </Group>
      </Group>

      <Tabs defaultValue="installed">
        <Tabs.List mb="md">
          <Tabs.Tab value="installed" leftSection={<IconShieldCheck size={14} />}>
            Installed Packs
          </Tabs.Tab>
          <Tabs.Tab value="recommendations" leftSection={<IconRobot size={14} />}>
            Curator Recommendations
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="installed">
          <InstalledPacksTab
            packs={packs}
            loading={packsLoading}
            error={packsError?.message ?? null}
          />
        </Tabs.Panel>

        <Tabs.Panel value="recommendations">
          {tenantId ? (
            <CuratorRecommendationsTab tenantId={tenantId} />
          ) : (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
              No tenant context available. Please log in to view recommendations.
            </Alert>
          )}
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default StandardsPackPage;
